/**
 * Contract B (worker remote-runtime) plugin-side React root.
 *
 * # The problem this solves
 *
 * A Contract B plugin runs in a sandboxed Web Worker — no `document`, no
 * `window`, no DOM. The host owns rendering: the worker constructs a
 * `RemoteElement` tree via `@remote-dom/core`, mutations forward over a
 * `MessagePort`, the host receives them and commits them to a real React tree
 * (see `coreconnect-web/src/extensions/runtime/WorkerSurfaceMount.tsx` for the
 * receiver side).
 *
 * Until now plugin authors had to hand-author the `RemoteElement` construction
 * + mutation forwarding manually. `@remote-dom/react` ships only the host-side
 * primitives (`createRemoteComponent`, `RemoteRootRenderer`, etc.) — there is
 * no worker-side React reconciler in the package. This helper provides one.
 *
 * # What this is
 *
 * The public surface (`createRemoteRoot(port, options)`) plus a working
 * `react-reconciler` HostConfig that commits to a Remote DOM tree and
 * forwards mutation records over the `MessagePort` to the host receiver.
 * Authors call `root.render(<App />)` and the host's `WorkerSurfaceMount`
 * sees the result.
 *
 * # What's NOT plumbed yet (Phase 1 limitation)
 *
 * Event-listener round-trip. The host transport currently has no
 * `ethisys:remotedom:call` channel — `WorkerRemoteDomTransport` only
 * forwards `ethisys:remotedom` payloads from worker → host, never the
 * other direction. So a worker-side `onClick={() => ...}` is **registered
 * locally** but the host can't call it. The reconciler retains every
 * listener on its in-memory `RemoteElementInstance` so the wiring is ready
 * the moment the call channel lands; until then, plugin authors should
 * keep interactive surfaces on Contract A.
 *
 * # The API
 *
 * ```ts
 * import { createRemoteRoot } from "@ethisyscore/extension-runtime/plugin";
 *
 * export async function activate(port: MessagePort): Promise<void> {
 *   const root = createRemoteRoot(port);
 *   root.render(<App />);
 * }
 * ```
 *
 * `<App />` is plain React. Any component shipped by the host's frozen
 * import-map allowlist (the closed Contract B primitive vocabulary —
 * `Button`, `DataTable`, `Form`, `Card`, `Tabs`, `Select`, `Alert`, etc.)
 * renders. The reconciler walks the React tree and commits to a
 * `RemoteRootElement`; mutation records forward over the port; the host's
 * `RemoteReceiver` commits the result into the real React tree.
 *
 * # Wire shape
 *
 * Every reconciler mutation is posted as
 * `{ type: "ethisys:remotedom", payload: RemoteMutationRecord[] }` to match
 * `WorkerRemoteDomTransport`'s `RemoteDomMessage` contract — the host
 * extracts `payload` and feeds it straight into `receiver.mutate(records)`.
 * `BatchingRemoteConnection` (controlled via `options.batchMutations`)
 * collapses contiguous mutations into a single port post per commit.
 */
import type { RemoteConnection, RemoteMutationRecord } from "@remote-dom/core";
import { BatchingRemoteConnection } from "@remote-dom/core/elements";
import type { ReactNode } from "react";
import {
  createReactReconciler,
  LegacyRoot,
  type RemoteRootContainer,
} from "./reconciler";

/**
 * Options for {@link createRemoteRoot}. Reserved for forward compatibility —
 * Phase 1 ships with zero required options. Adding fields here is additive;
 * removing them is a breaking change.
 */
export interface CreateRemoteRootOptions
{
    /**
     * When supplied, the reconciler batches contiguous mutations into a single
     * `port.postMessage` rather than firing one per mutation. Default `true`
     * — measurably reduces host-side commit cost on the first render of a
     * non-trivial tree.
     */
    batchMutations?: boolean;

    /**
     * Override the connection factory. Reserved for tests; production callers
     * never pass this. The factory's contract is "build a RemoteConnection
     * that forwards mutations over the supplied port"; the default uses
     * `createRemoteConnection` from `@remote-dom/core`.
     */
    connectionFactory?: (port: MessagePort) => RemoteConnection;
}

/**
 * Plugin-side React root. Mirrors the shape of `ReactDOMClient.Root` so
 * authors familiar with `createRoot().render(...)` find the same API on the
 * worker side.
 */
export interface RemoteRoot
{
    /**
     * Render a React element tree against the worker's `RemoteRootElement`.
     * The reconciler commits to the root, the mutation observer forwards
     * mutations over the port, the host re-renders. Idempotent — calling
     * `render` twice with the same element is fine; the reconciler dedupes.
     */
    render(element: ReactNode): void;

    /**
     * Tear down the reconciled tree and stop forwarding mutations. Authors
     * should call this from a `Symbol.dispose` or equivalent when the worker
     * is shutting down — leaking the reconciler holds the `MessagePort` open
     * and prevents the worker from being collected.
     */
    unmount(): void;
}

/**
 * Construct a plugin-side React root that commits to a `RemoteRootElement`
 * and forwards mutations over the supplied `MessagePort`.
 *
 * **API stability:** the function signature is stable for Phase 1. The
 * options bag is forward-compatible (additive only).
 *
 * **Implementation status:** the connection + root construction lands in this
 * commit. The `react-reconciler` `HostConfig` is a scaffold — `render()`
 * throws a structured error directing authors to the W1A tracking issue.
 * Authors should treat this commit as "the API is locked, the reconciler is
 * being authored." See follow-on commits on the `feature/contract-b-create-remote-root-w1a`
 * branch.
 */
export function createRemoteRoot(
    port: MessagePort,
    options: CreateRemoteRootOptions = {},
): RemoteRoot
{
    if (port === undefined || port === null)
    {
        throw new TypeError(
            "[createRemoteRoot] `port` is required. Pass the MessagePort the host transferred via the worker's `activate(port)` entry point.",
        );
    }

    // Build the connection that forwards Remote DOM mutations over the port.
    // The default factory uses `@remote-dom/core`'s `createRemoteConnection`;
    // tests inject their own factory via `options.connectionFactory`.
    const baseConnection = (options.connectionFactory ?? defaultConnectionFactory)(port);

    const connection: RemoteConnection = options.batchMutations === false
        ? baseConnection
        : new BatchingRemoteConnection(baseConnection);

    // W1A — lazy reconciler + container construction. The reconciler is built
    // once on first render(); subsequent calls reuse the same container so React
    // commits behave like ReactDOM.createRoot(...).render(...) — call render
    // multiple times to update; call unmount to tear down.
    const { reconciler, createContainer } = createReactReconciler(connection);
    let container: RemoteRootContainer | null = null;
    let fiberRoot: ReturnType<typeof reconciler.createContainer> | null = null;

    return {
        render(element: ReactNode): void
        {
            if (container === null)
            {
                container = createContainer();
                fiberRoot = reconciler.createContainer(
                    container,
                    LegacyRoot,
                    null, // hydration callbacks
                    false, // isStrictMode
                    null, // concurrentUpdatesByDefaultOverride
                    "", // identifierPrefix
                    (error: Error) =>
                    {
                        // The host's SurfaceErrorBoundary catches render-time
                        // failures via the connection's call() path; mirroring
                        // them locally as a console.error is the best we can do
                        // from inside the worker.
                        // eslint-disable-next-line no-console -- worker console is pumped to the host logger.
                        console.error("[createRemoteRoot] render error", error);
                    },
                    null, // transitionCallbacks
                );
            }
            reconciler.updateContainer(element, fiberRoot!, null, null);
        },
        unmount(): void
        {
            if (fiberRoot !== null)
            {
                reconciler.updateContainer(null, fiberRoot, null, null);
                fiberRoot = null;
                container = null;
            }
            // The connection itself does NOT close the port — plugins may have
            // side-channel consumers. Authors that want a hard tear-down call
            // `port.close()` themselves after unmount().
        },
    };
}

// ── Internals ──────────────────────────────────────────────────────────

/**
 * Default factory: build a `RemoteConnection` that posts mutation records
 * as the `payload` of a single `ethisys:remotedom` port message — the
 * wire shape `WorkerRemoteDomTransport`'s `RemoteDomMessage` declares
 * (see `host/worker/transport.ts`'s `handlePortMessage`: it forwards
 * `(message as RemoteDomMessage).payload` straight into the host's
 * `RemoteReceiver.mutate(records)` call).
 *
 * Going through `createRemoteConnection` (which fires per-op callbacks)
 * was the wrong abstraction for this wire — the host expects the raw
 * record list under `payload`, not bespoke `op`-suffixed envelopes. By
 * implementing the connection directly we hand the reconciler's
 * mutation records straight to the host receiver without translation.
 *
 * Wrapping in `BatchingRemoteConnection` (controlled via
 * `options.batchMutations`) collapses contiguous mutation calls into a
 * single `port.postMessage` payload, which is the difference between "one
 * postMessage per appended child" and "one postMessage per React commit"
 * — measurably cheaper for the first paint of a non-trivial tree.
 */
function defaultConnectionFactory(port: MessagePort): RemoteConnection
{
    return {
        mutate(records: readonly RemoteMutationRecord[]): void
        {
            // Snapshot to a plain array so the host receiver isn't handed a
            // reference into the reconciler's internal buffer (the BatchingRemoteConnection
            // wrapper recycles its own array between flushes).
            port.postMessage({ type: "ethisys:remotedom", payload: records.slice() });
        },
        call(_id, _method, ..._args): unknown
        {
            // Worker → host method invocation. Reserved for the future
            // `ethisys:remotedom:call` channel — `WorkerRemoteDomTransport`
            // doesn't yet handle a `call`-shaped reply path, so event-
            // listener round-trip is still on the Phase 1 follow-on list
            // (see this file's top-of-file "What's NOT plumbed yet" note).
            return undefined;
        },
    };
}

